package com.vamvisions.main.theme

import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.media

private const val PLAYFAIR = "\"Playfair Display\", Georgia, serif"

/** Viewports narrower than 600 CSS pixels: phones, where cards stack and the navigation collapses. */
const val NARROW_SCREEN = "(max-width: 599.98px)"

/** The smallest tap target, in both directions. */
private const val MIN_TAP_SIZE = "44px"

/** CSS custom property names. Components reference colours and spacing only through these. */
object Tokens {
    const val COLOR_BG = "--color-bg"
    const val COLOR_SURFACE = "--color-surface"

    /** The page frame (header, main panel, footer) over the intro background. */
    const val COLOR_SURFACE_TRANSLUCENT = "--color-surface-translucent"

    /** Dims the intro background, between it and the page. */
    const val COLOR_BG_OVERLAY = "--color-bg-overlay"
    const val COLOR_ON_BG = "--color-on-bg"
    const val COLOR_BRAND = "--color-brand"
    const val COLOR_MUTED = "--color-muted"
    const val COLOR_CHIP = "--color-chip"

    /** Glass card fill and stroke, over the intro background. */
    const val COLOR_GLASS = "--color-glass"
    const val COLOR_GLASS_STROKE = "--color-glass-stroke"

    /** Text on a brand-coloured fill. */
    const val COLOR_ON_BRAND = "--color-on-brand"

    /** Form validation and send errors. */
    const val COLOR_ERROR = "--color-error"
    const val SHADOW_CARD = "--shadow-card"
    const val SHADOW_GLASS = "--shadow-glass"
    const val SPACE_1 = "--space-1"
    const val SPACE_2 = "--space-2"
    const val SPACE_3 = "--space-3"
    const val SPACE_4 = "--space-4"
    const val RADIUS = "--radius"
    const val CONTAINER_MAX_WIDTH = "--container-max-width"
}

fun cssVar(name: String) = "var($name)"

/**
 * The single definition of the colour schemes and global styles. `<html data-theme>` selects the
 * scheme; without it, `prefers-color-scheme` decides.
 */
object AppStyleSheet : StyleSheet() {

    init {
        ":root, :root[data-theme=light]" style {
            lightColors()
            property("color-scheme", "light")
            property(Tokens.SPACE_1, "0.25rem")
            property(Tokens.SPACE_2, "0.5rem")
            property(Tokens.SPACE_3, "1rem")
            property(Tokens.SPACE_4, "2rem")
            property(Tokens.RADIUS, "0.5rem")
            property(Tokens.CONTAINER_MAX_WIDTH, "60rem")
        }
        ":root[data-theme=dark]" style {
            darkColors()
            property("color-scheme", "dark")
        }
        media("(prefers-color-scheme: dark)") {
            ":root:not([data-theme=light])" style {
                darkColors()
                property("color-scheme", "dark")
            }
        }

        "*, *::before, *::after" style {
            property("box-sizing", "border-box")
        }
        "html" style {
            property("font-size", "18px")
            property("-webkit-text-size-adjust", "100%")
        }
        "body" style {
            property("margin", "0")
            // The fonts are declared in styles.css. Until they load, or if they fail to, the system font shows.
            property(
                "font-family",
                "Montserrat, system-ui, -apple-system, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif",
            )
            property("line-height", "1.5")
            property("background-color", cssVar(Tokens.COLOR_BG))
            property("color", cssVar(Tokens.COLOR_ON_BG))
        }
        "h1, h2, h3" style {
            property("font-family", PLAYFAIR)
        }
        "a" style {
            property("color", cssVar(Tokens.COLOR_BRAND))
        }
        ":focus-visible" style {
            property("outline", "3px solid ${cssVar(Tokens.COLOR_BRAND)}")
            property("outline-offset", "2px")
        }
        media("(prefers-reduced-motion: reduce)") {
            "*, *::before, *::after" style {
                property("animation-duration", "0.01ms", important = true)
                property("animation-iteration-count", "1", important = true)
                property("transition-duration", "0.01ms", important = true)
                property("scroll-behavior", "auto", important = true)
            }
        }
    }

    /** Centres content and caps its width, with side padding on narrow screens. */
    val container by style {
        property("width", "100%")
        property("max-width", cssVar(Tokens.CONTAINER_MAX_WIDTH))
        property("margin-inline", "auto")
        property("padding-inline", cssVar(Tokens.SPACE_3))
    }

    // Components

    /** A surface panel: [com.vamvisions.main.components.Card]. */
    val card by style {
        property("padding", cssVar(Tokens.SPACE_3))
        property("background-color", cssVar(Tokens.COLOR_SURFACE))
        property("border-radius", "1rem")
        property("box-shadow", cssVar(Tokens.SHADOW_CARD))
        property("min-width", "0")
    }

    /**
     * A glass surface over the intro background: [com.vamvisions.main.components.Card] with `glass = true`. Declared
     * after [card], so its fill and shadow win. Browsers without backdrop blur get the solid surface colour.
     */
    val glass by style {
        property("background-color", cssVar(Tokens.COLOR_GLASS))
        property("border", "1px solid ${cssVar(Tokens.COLOR_GLASS_STROKE)}")
        property("border-radius", "1rem")
        property("box-shadow", cssVar(Tokens.SHADOW_GLASS))
        property("-webkit-backdrop-filter", "blur(12px)")
        property("backdrop-filter", "blur(12px)")
    }

    /** A card title that is not a heading element, in the heading font. */
    val cardTitle by style {
        property("font-family", PLAYFAIR)
        property("font-weight", "700")
        property("line-height", "1.2")
    }

    val section by style {
        property("margin-block", "${cssVar(Tokens.SPACE_4)} 0")
    }

    val sectionTitle by style {
        property("margin", "0 0 ${cssVar(Tokens.SPACE_3)}")
        property("font-size", "1.4rem")
    }

    /** Groups a [sectionEyebrow] with its [sectionTitle]; the group, not the title, keeps the space below. */
    val sectionHeading by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "0.22rem")
        property("margin", "0 0 ${cssVar(Tokens.SPACE_3)}")
        desc(self, className(sectionTitle)) style {
            property("margin", "0")
        }
    }

    /** The small label above a section title. */
    val sectionEyebrow by style {
        property("margin", "0")
        property("font-size", "0.72rem")
        property("font-weight", "700")
        property("letter-spacing", "0.12em")
        property("text-transform", "uppercase")
        property("color", cssVar(Tokens.COLOR_BRAND))
    }

    /** Wrapping pills. The list positions the tooltips, so a tooltip is as wide as the list, not its pill. */
    val skillList by style {
        property("position", "relative")
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", cssVar(Tokens.SPACE_2))
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
    }

    val skillItem by style {
        property("min-width", "0")
        property("max-width", "100%")
    }

    val skillLink by style {
        property("display", "block")
        property("padding", "0.28rem 0.67rem")
        property("font-size", "0.72rem")
        property("font-weight", "600")
        property("text-decoration", "none")
        property("color", cssVar(Tokens.COLOR_ON_BG))
        property("background-color", cssVar(Tokens.COLOR_CHIP))
        property("border-radius", "999px")
        property("overflow-wrap", "anywhere")
        property("transition", "transform 0.3s")
        self + ":hover" style {
            property("transform", "scale(1.05)")
        }
    }

    /**
     * Hidden until its skill link is hovered or keyboard-focused; it stays the link's accessible
     * description. It is as wide as its text, and never wider than the list, so it never runs past the viewport edge.
     * [com.vamvisions.main.components.SkillList] moves it just below its pill as it is shown.
     */
    val skillTooltip by style {
        property("display", "none")
        property("position", "absolute")
        property("top", "calc(100% + ${cssVar(Tokens.SPACE_1)})")
        property("left", "0")
        property("width", "max-content")
        property("max-width", "100%")
        property("z-index", "5")
        property("padding", "${cssVar(Tokens.SPACE_1)} ${cssVar(Tokens.SPACE_2)}")
        property("font-size", "0.85rem")
        property("color", cssVar(Tokens.COLOR_BG))
        property("background-color", cssVar(Tokens.COLOR_ON_BG))
        property("border-radius", cssVar(Tokens.RADIUS))
        property("overflow-wrap", "anywhere")
        property("pointer-events", "none")
    }

    /** An anchor that looks like a button: every one of these is a navigation. */
    val linkButton by style {
        property("display", "inline-flex")
        property("align-items", "center")
        property("min-height", "2.75rem")
        property("padding", "0 ${cssVar(Tokens.SPACE_3)}")
        property("color", cssVar(Tokens.COLOR_BRAND))
        property("text-decoration", "none")
        property("border", "1px solid ${cssVar(Tokens.COLOR_BRAND)}")
        property("border-radius", cssVar(Tokens.RADIUS))
        property("transition", "background-color 0.2s")
        self + ":hover" style {
            property("background-color", cssVar(Tokens.COLOR_CHIP))
        }
    }

    /** Added to [linkButton]: the filled main call to action. */
    val linkButtonPrimary by style {
        property("gap", "0.55rem")
        property("min-height", "2.78rem")
        property("padding", "0 2rem")
        property("font-size", "0.89rem")
        property("font-weight", "600")
        property("color", cssVar(Tokens.COLOR_ON_BRAND))
        property("background-color", cssVar(Tokens.COLOR_BRAND))
        property("border", "none")
        property("border-radius", "999px")
        property("box-shadow", "0 6px 18px color-mix(in srgb, ${cssVar(Tokens.COLOR_BRAND)} 25%, transparent)")
        property("transition", "filter 0.2s")
        self + ":hover" style {
            property("background-color", cssVar(Tokens.COLOR_BRAND))
            property("filter", "brightness(1.12)")
        }
        // The arrow after the label.
        desc(self, "span") style {
            property("font-size", "1rem")
        }
    }

    /**
     * A rounded outline link with an optional icon before its label. Its tap area reaches 44 CSS pixels tall, above
     * and below the pill, so a row of pills keeps the Figma size and still meets the minimum touch target.
     */
    val linkPill by style {
        property("position", "relative")
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "0.39rem")
        property("min-height", "2.11rem")
        property("padding", "0 0.72rem")
        property("font-size", "0.72rem")
        property("font-weight", "600")
        property("color", cssVar(Tokens.COLOR_BRAND))
        property("text-decoration", "none")
        property("border", "1px solid ${cssVar(Tokens.COLOR_BRAND)}")
        property("border-radius", "999px")
        property("transition", "background-color 0.2s")
        self + ":hover" style {
            property("background-color", cssVar(Tokens.COLOR_CHIP))
        }
        desc(self, "svg") style {
            property("display", "block")
            property("flex-shrink", "0")
            property("width", "0.89rem")
            property("height", "0.89rem")
        }
        self + "::before" style {
            property("content", "\"\"")
            property("position", "absolute")
            property("inset", "min(0px, calc((100% - $MIN_TAP_SIZE) / 2)) 0")
        }
    }

    /** [com.vamvisions.main.components.ConnectCard]: text and button side by side, stacked and centred on phones. */
    val connectCard by style {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "1.33rem")
        property("padding", "1.22rem 1.56rem")
        media(NARROW_SCREEN) {
            self style {
                property("flex-direction", "column")
                property("gap", "0.89rem")
                property("padding", "1.33rem 1.22rem")
                property("text-align", "center")
            }
        }
    }

    val connectText by style {
        property("display", "flex")
        property("flex", "1")
        property("flex-direction", "column")
        property("gap", "0.22rem")
        property("min-width", "0")
    }

    val connectTitle by style {
        property("margin", "0")
        property("font-size", "1.44rem")
        property("line-height", "1.3")
        media(NARROW_SCREEN) {
            self style {
                property("font-size", "1.33rem")
            }
        }
    }

    val connectBody by style {
        property("margin", "0")
        property("font-size", "0.89rem")
        property("color", cssVar(Tokens.COLOR_MUTED))
    }

    /** Holds the primary link, full width on phones. */
    val connectButton by style {
        property("flex-shrink", "0")
        property("border-radius", "999px")
        media(NARROW_SCREEN) {
            self style {
                property("width", "100%")
            }
            desc(self, "a") style {
                property("width", "100%")
                property("justify-content", "center")
            }
        }
    }

    /** A small filled label, such as "Coming soon". */
    val statusBadge by style {
        property("display", "inline-block")
        property("align-self", "center")
        property("padding", "0.17rem 0.56rem")
        property("font-size", "0.61rem")
        property("font-weight", "700")
        property("letter-spacing", "0.08em")
        property("text-transform", "uppercase")
        property("line-height", "1.2")
        property("color", cssVar(Tokens.COLOR_ON_BRAND))
        property("background-color", cssVar(Tokens.COLOR_BRAND))
        property("border-radius", "999px")
    }

    val button by style {
        property("min-height", "2.75rem")
        property("padding", "0 ${cssVar(Tokens.SPACE_3)}")
        property("font", "inherit")
        property("color", cssVar(Tokens.COLOR_BRAND))
        property("background", "transparent")
        property("border", "1px solid ${cssVar(Tokens.COLOR_BRAND)}")
        property("border-radius", cssVar(Tokens.RADIUS))
        property("cursor", "pointer")
        self + ":hover" style {
            property("background-color", cssVar(Tokens.COLOR_CHIP))
        }
        self + ":disabled" style {
            property("cursor", "not-allowed")
            property("opacity", "0.6")
        }
    }

    /**
     * Reveal on scroll ([com.vamvisions.main.theme.rememberReveal]). The hidden start state applies only under `html.js-reveal`,
     * which is set only when the observer exists and reduced motion is off, so content never stays hidden.
     */
    val reveal by style {
        property("transition", "opacity 0.6s ease-out, transform 0.6s ease-out")
    }

    /** Added once a [reveal] element has scrolled into view. */
    val revealed by style {}

    /**
     * [com.vamvisions.main.components.background.IntroBackground]: fixed behind the page, so it never
     * takes part in layout or scrolling, and never receives the pointer.
     */
    val introBackground by style {
        property("position", "fixed")
        property("inset", "0")
        property("z-index", "-1")
        property("overflow", "hidden")
        property("pointer-events", "none")
        // Second guard: the component does not create the video when reduced motion is requested.
        media(REDUCED_MOTION_QUERY) {
            desc(self, "video") style {
                property("display", "none")
            }
        }
    }

    /**
     * The poster and the video, stacked; `cover` fills any aspect ratio. Anchored left so narrow screens crop the
     * right side and keep the tree in the artwork's top-left corner.
     */
    val introBackgroundMedia by style {
        property("position", "absolute")
        property("inset", "0")
        property("width", "100%")
        property("height", "100%")
        property("object-fit", "cover")
        property("object-position", "left center")
    }

    val introBackgroundOverlay by style {
        property("position", "absolute")
        property("inset", "0")
        property("background-color", cssVar(Tokens.COLOR_BG_OVERLAY))
    }

    /** Read by assistive technology but not shown, for example the static text behind an animation. */
    val srOnly by style {
        property("position", "absolute")
        property("width", "1px")
        property("height", "1px")
        property("margin", "-1px")
        property("padding", "0")
        property("overflow", "hidden")
        property("clip", "rect(0 0 0 0)")
        property("white-space", "nowrap")
        property("border", "0")
    }

    val statusMessage by style {
        property("padding-block", cssVar(Tokens.SPACE_4))
        property("color", cssVar(Tokens.COLOR_MUTED))
    }

    init {
        // Rules that combine the classes above, so they come after the class declarations.
        adjacent(className(skillLink) + ":hover", className(skillTooltip)) style {
            property("display", "block")
        }
        adjacent(className(skillLink) + ":focus-visible", className(skillTooltip)) style {
            property("display", "block")
        }
        "html.$REVEAL_GATE_CLASS .$reveal:not(.$revealed)" style {
            property("opacity", "0")
            property("transform", "translateY(16px)")
        }
        media(REDUCED_MOTION_QUERY) {
            ".$reveal" style {
                property("opacity", "1", important = true)
                property("transform", "none", important = true)
                property("transition", "none")
            }
        }
        supports("not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))") {
            ".$glass" style {
                property("background-color", cssVar(Tokens.COLOR_SURFACE))
            }
        }
    }

    private fun org.jetbrains.compose.web.css.StyleScope.lightColors() {
        property(Tokens.COLOR_BG, "#FAFAFA")
        property(Tokens.COLOR_SURFACE, "#F4F6FA")
        property(Tokens.COLOR_SURFACE_TRANSLUCENT, "rgb(244 246 250 / 0.6)")
        property(Tokens.COLOR_BG_OVERLAY, "rgb(250 250 250 / 0.5)")
        property(Tokens.COLOR_ON_BG, "#000000")
        // Brand and muted are dark enough for 4.5:1 over the darkest spot of the background.
        property(Tokens.COLOR_BRAND, "#375A41")
        property(Tokens.COLOR_MUTED, "#4B525C")
        property(Tokens.COLOR_CHIP, "#E4E8EE")
        property(Tokens.COLOR_GLASS, "rgb(255 255 255 / 0.55)")
        property(Tokens.COLOR_GLASS_STROKE, "rgb(255 255 255 / 0.8)")
        property(Tokens.COLOR_ON_BRAND, "#FAFAFA")
        property(Tokens.COLOR_ERROR, "#B3261E")
        property(Tokens.SHADOW_CARD, "0 2px 10px rgb(0 0 0 / 0.06)")
        property(Tokens.SHADOW_GLASS, "0 8px 24px rgb(0 0 0 / 0.08)")
    }

    private fun org.jetbrains.compose.web.css.StyleScope.darkColors() {
        property(Tokens.COLOR_BG, "#06080B")
        property(Tokens.COLOR_SURFACE, "#13171F")
        property(Tokens.COLOR_SURFACE_TRANSLUCENT, "rgb(19 23 31 / 0.6)")
        property(Tokens.COLOR_BG_OVERLAY, "rgb(6 8 11 / 0.4)")
        property(Tokens.COLOR_ON_BG, "#FFFFFF")
        property(Tokens.COLOR_BRAND, "#90AB9A")
        property(Tokens.COLOR_MUTED, "#A3ABB6")
        property(Tokens.COLOR_CHIP, "#262C37")
        property(Tokens.COLOR_GLASS, "rgb(19 23 31 / 0.55)")
        property(Tokens.COLOR_GLASS_STROKE, "rgb(255 255 255 / 0.12)")
        property(Tokens.COLOR_ON_BRAND, "#06080B")
        property(Tokens.COLOR_ERROR, "#F2B8B5")
        property(Tokens.SHADOW_CARD, "0 2px 10px rgb(0 0 0 / 0.5)")
        property(Tokens.SHADOW_GLASS, "0 8px 24px rgb(0 0 0 / 0.08)")
    }
}
